import {creditCheck, type CreditCheck} from '../../creditCheck';
import {authorReasons} from '../../errors';
import {keptReels} from '../../eta';
import {
  keepsEveryReel,
  partialRender,
  plannedReelSeconds,
  previewProgress,
  renderBlocker,
  renderBlockerText,
  unmadeReels,
  type PartialRender,
  type RenderBlocker,
} from '../../reels';
import {studioText} from '../../studioText';
import {isClippingJob, isVideoJob} from '../../videoSource';
import type {Issue, JobView, Reel} from '../../types';
import {storyboardText} from './storyboardText';

/** A microreel holds at most this many scenes (the server's limit for adding one). */
export const REEL_SCENE_MAX = 20;
/** The longest narration a new scene takes (the server's limit). */
export const NEW_SCENE_NARRATION_MAX = 600;
/** The longest on-screen text a new scene takes, as for any scene. */
export const NEW_SCENE_ON_SCREEN_MAX = 100;
export const REEL_TITLE_MAX = 80;

/** The job's render is of its current storyboard. */
export const isRenderCurrent = (view: JobView) =>
  view.job.status === 'done' && !!view.media;

/** What the page knows about the job, worked out once for every part of it. */
export interface StoryboardFacts {
  reels: Reel[];
  count: number;
  video: boolean;
  /** Clipping microreels are cut from the video: their scenes are clips of it. */
  clipping: boolean;
  /** The ending after each microreel: a policy attestation rather than a knowledge check. */
  attestation: boolean;
  hasEnding: boolean;
  renderCount: number;
  /** The render is of this storyboard. */
  upToDate: boolean;
  /** Endings are not part of the video: unsaved ones on a current render only need saving. */
  savesOnly: boolean;
  onlyWatch: boolean;
  /** A microreel changed since the last render. */
  edited: boolean;
  /**
   * A render that stopped part way: the microreels it made and the ones it did not. When the
   * server keeps the ones made (`continues`), generating finishes the rest.
   */
  partial: PartialRender | null;
  /** A render stopped after it made every microreel: generating keeps them and finishes them. */
  keepsAll: boolean;
  /**
   * A render that stopped part way made some microreels, but generating keeps none of them:
   * the ones no render made, which it makes first.
   */
  unmade: Reel[];
  /**
   * While generating: the microreels it keeps from a render that stopped part way, which play
   * already. It makes the rest.
   */
  kept: number;
  /** A render made microreels that can be watched: a finished one, or one that stopped part way. */
  watchable: boolean;
  totalSeconds: number;
  creditCheckPending: boolean;
  /** Why AI actions wait for the last generation's AI credits to settle; null when they need not. */
  creditCheck: CreditCheck | null;
  blockers: RenderBlocker[];
  /** Why renaming (and generating) waits, while Studio works on the job. */
  waitReason?: string;
  /** Why Generate is unavailable, or what it will do first; empty when neither. */
  generateReason: string;
}

export interface PageState {
  busy: boolean;
  drafting: boolean;
  locked: boolean;
  dirty: boolean;
  pendingEndings: number;
}

export const sceneCountText = (count: number, clipping: boolean) =>
  storyboardText.sceneCount(count, clipping);

/** `canAddCredits`: the author may buy AI credits, which a pending credit check says. */
export function storyboardFacts(
  view: JobView,
  state: PageState,
  canAddCredits = false,
): StoryboardFacts {
  const {job} = view;
  const reels = job.storyboard?.reels || [];
  const count = reels.length;
  const clipping = isClippingJob(job);
  const attestation = job.brief.ending === 'policy_attestation';
  const renderCount = job.renderCount ?? 0;
  const upToDate = isRenderCurrent(view);
  const savesOnly = upToDate && state.pendingEndings > 0;
  const credits = creditCheck(view, canAddCredits);
  const creditCheckPending = !!credits;
  const sceneNoun = clipping ? studioText.clipNoun : studioText.sceneNoun;
  const blockers = (
    credits ? [credits.text] : authorReasons(view.renderBlockers)
  ).map(reason => renderBlocker(reels, reason, sceneNoun));
  const waitReason = state.drafting
    ? storyboardText.waitForDrafting
    : state.locked
    ? storyboardText.waitForGenerating(count)
    : view.running
    ? storyboardText.waitForStudio
    : undefined;
  const generateReason = state.busy
    ? waitReason || ''
    : state.dirty
    ? storyboardText.saveEditsFirst(clipping)
    : !view.canRender || creditCheckPending
    ? blockers.map(renderBlockerText).join('\n')
    : state.pendingEndings > 0
    ? storyboardText.endingSavedOnGenerate(attestation)
    : '';
  return {
    reels,
    count,
    video: isVideoJob(job),
    clipping,
    attestation,
    hasEnding: job.brief.ending !== 'none',
    renderCount,
    upToDate,
    savesOnly,
    onlyWatch: upToDate && !savesOnly,
    edited: reels.some(r => r.status === 'edited') && renderCount > 0,
    partial: partialRender(view),
    keepsAll: keepsEveryReel(view),
    unmade: unmadeReels(view),
    kept: job.status === 'rendering' ? keptReels(job).length : 0,
    watchable: renderCount > 0 || previewProgress(view).ready > 0,
    totalSeconds: reels.reduce(
      (total, reel) => total + plannedReelSeconds(reel),
      0,
    ),
    creditCheckPending,
    creditCheck: credits,
    blockers,
    waitReason,
    generateReason,
  };
}

/** The microreel's errors to fix, other than its ending's. */
export const reelErrorCount = (view: JobView, reelId: string) =>
  (view.issues || []).filter(
    i => i.reelId === reelId && i.severity === 'error' && i.scope !== 'ending',
  ).length;

/** The scenes' own issues, by scene id; a microreel's own under `reel:<id>`. */
export function issuesByScene(view: JobView): Record<string, Issue[]> {
  const map: Record<string, Issue[]> = {};
  for (const issue of view.issues || []) {
    const key = issue.sceneId || `reel:${issue.reelId}`;
    (map[key] ||= []).push(issue);
  }
  return map;
}

// The source check's verdicts ("Check: r1-s3 may not be supported by the source. …") and its
// "Accuracy check unavailable …" notes are hidden from authors for now.
const isSourceCheckNote = (message: string) =>
  /^\s*(?:Check: \S+ may not be supported by the source|Accuracy check unavailable)/i.test(
    message,
  );

/**
 * A microreel's own notes belong to no scene or clip: a clip reel's length and how its clips
 * were chosen. The ending editor and the confirmation panel show theirs.
 */
export const reelNotes = (byScene: Record<string, Issue[]>, reel: Reel) =>
  (byScene[`reel:${reel.id}`] || []).filter(
    i =>
      i.scope !== 'ending' &&
      !/needs sme/i.test(i.message) &&
      !/^\s*resolved:/i.test(i.message) &&
      !isSourceCheckNote(i.message),
  );

/**
 * The author adds scenes to a microreel of scenes; a clip reel takes clips cut from the video.
 * A microreel with no scenes yet is a clip reel when its job cuts clips.
 */
export const takesNewScenes = (reel: Reel, clipping: boolean) =>
  reel.scenes.length > 0
    ? !reel.scenes.every(scene => scene.layout === 'clip')
    : !clipping;
